import { useEffect, useLayoutEffect, useRef } from 'react';
import { ownsKeys, shortcutFor, type Shortcut } from './shortcuts';

/** What each page key does; `null` for a key the page does not offer (full screen where it is not enabled). */
export type ShortcutActions = Record<Shortcut, (() => void) | null>;

/**
 * The page keys (`/`, `c`, `f`) on the document, so they work from arrival with nothing focused, until the page
 * unmounts. A key typed into a field, an editable element, or an open menu or listbox is left alone, as is a held
 * key's repeat (holding `f` would flip full screen on and off); only a key the page acts on is prevented.
 * Escape stays with the canvas.
 */
export function usePageShortcuts(actions: ShortcutActions) {
  // The latest actions, so the listener is added once.
  const latest = useRef(actions);
  useLayoutEffect(() => {
    latest.current = actions;
  });
  useEffect(() => {
    const keyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.defaultPrevented) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      const modified = event.ctrlKey || event.metaKey || event.altKey;
      const shortcut = shortcutFor(event.key, target?.tagName ?? '', {
        modified,
        ownsKeys: target !== null && ownsKeys(target),
      });
      const action = shortcut === null ? null : latest.current[shortcut];
      if (!action) return;
      event.preventDefault();
      action();
    };
    document.addEventListener('keydown', keyDown);
    return () => document.removeEventListener('keydown', keyDown);
  }, []);
}
